import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2, Upload, X, ImageIcon } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteFormSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service_type: "",
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    postcode: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB per file.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImages((prev) => [
          ...prev,
          { name: file.name, preview: e.target.result, file, uploaded: false, fileId: null },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    const uploadedFiles = [];
    
    for (const image of images) {
      if (image.uploaded && image.fileId) {
        uploadedFiles.push(image.fileId);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', image.file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedFiles.push(response.data.id);
      } catch (error) {
        // Log error for debugging but don't expose to console in production
        if (process.env.NODE_ENV === 'development') {
          console.error(`Failed to upload ${image.name}:`, error);
        }
        toast.error(`Failed to upload ${image.name}`);
      }
    }
    
    return uploadedFiles;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Upload images first
      let imageIds = [];
      if (images.length > 0) {
        toast.info("Uploading images...");
        imageIds = await uploadImages();
      }
      
      const payload = {
        ...formData,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        image_ids: imageIds,
      };

      await axios.post(`${API}/quotes`, payload);
      
      toast.success("Quote request submitted!", {
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({
        name: "",
        email: "",
        service_type: "",
        property_type: "",
        bedrooms: "",
        bathrooms: "",
        postcode: "",
        message: "",
      });
      setImages([]);
    } catch (error) {
      // Log error for debugging but don't expose to console in production
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting quote:", error);
      }
      toast.error("Failed to submit quote", {
        description: "Please try again or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceTypes = [
    { value: "regular", label: "Regular Clean" },
    { value: "detailed", label: "Detailed Clean" },
    { value: "deep", label: "Deep Clean / End of Tenancy" },
    { value: "commercial", label: "Commercial Services" },
  ];

  const propertyTypes = [
    { value: "house", label: "House" },
    { value: "flat", label: "Flat / Apartment" },
    { value: "student", label: "Student Accommodation" },
    { value: "office", label: "Office / Commercial" },
  ];

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-20 lg:py-32 bg-[#F8FAFC] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 blue-glow rounded-full blur-3xl opacity-30" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white border border-[#3B82F6]/20 rounded-full px-4 py-2 mb-6 shadow-sm">
              <Send className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-sm font-medium text-[#3B82F6]">Free Quote</span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4 tracking-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Get Your Free Quote
            </h2>
            <p className="text-lg text-[#64748B] mb-8">
              Tell us about your cleaning needs and we'll get back to you with a personalised quote within 24 hours.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              {[
                "No obligation quote",
                "Response within 24 hours",
                "Competitive pricing",
                "Flexible scheduling",
              ].map((benefit) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#EFF6FF] rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-[#3B82F6]" />
                  </div>
                  <span className="text-[#0F172A] font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="mt-12 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <h3 
                className="font-bold text-[#0F172A] mb-2"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Prefer to email directly?
              </h3>
              <a
                href="mailto:info@detailedcleaningservices.co.uk"
                className="text-[#3B82F6] font-medium hover:underline"
                data-testid="direct-email-link"
              >
                info@detailedcleaningservices.co.uk
              </a>
            </div>
          </div>

          {/* Right Form */}
          <Card className="border border-slate-100 shadow-xl bg-white animate-fade-in-up stagger-2">
            <CardHeader className="pb-4">
              <CardTitle 
                className="text-2xl font-bold text-[#0F172A]"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Request a Quote
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name & Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#0F172A] font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-[#F8FAFC] border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20"
                      data-testid="quote-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#0F172A] font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-[#F8FAFC] border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20"
                      data-testid="quote-email-input"
                    />
                  </div>
                </div>

                {/* Service & Property Type */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium">Service Type *</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) => handleSelectChange("service_type", value)}
                      required
                    >
                      <SelectTrigger 
                        className="bg-[#F8FAFC] border-slate-200"
                        data-testid="quote-service-select"
                      >
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium">Property Type *</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => handleSelectChange("property_type", value)}
                      required
                    >
                      <SelectTrigger 
                        className="bg-[#F8FAFC] border-slate-200"
                        data-testid="quote-property-select"
                      >
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Bedrooms, Bathrooms, Postcode */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms" className="text-[#0F172A] font-medium">
                      Bedrooms
                    </Label>
                    <Select
                      value={formData.bedrooms}
                      onValueChange={(value) => handleSelectChange("bedrooms", value)}
                    >
                      <SelectTrigger 
                        className="bg-[#F8FAFC] border-slate-200"
                        data-testid="quote-bedrooms-select"
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, "6+"].map((num) => (
                          <SelectItem key={num} value={String(num === "6+" ? 6 : num)}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bathrooms" className="text-[#0F172A] font-medium">
                      Bathrooms
                    </Label>
                    <Select
                      value={formData.bathrooms}
                      onValueChange={(value) => handleSelectChange("bathrooms", value)}
                    >
                      <SelectTrigger 
                        className="bg-[#F8FAFC] border-slate-200"
                        data-testid="quote-bathrooms-select"
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, "5+"].map((num) => (
                          <SelectItem key={num} value={String(num === "5+" ? 5 : num)}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postcode" className="text-[#0F172A] font-medium">
                      Postcode *
                    </Label>
                    <Input
                      id="postcode"
                      name="postcode"
                      placeholder="S1 1AA"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      required
                      className="bg-[#F8FAFC] border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20"
                      data-testid="quote-postcode-input"
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">
                    Upload Photos (Optional)
                  </Label>
                  <p className="text-xs text-[#64748B] mb-2">
                    Add up to 5 photos of the space for a more accurate quote
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="quote-image-input"
                  />
                  
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 hover:border-[#3B82F6]/50 rounded-xl p-6 text-center cursor-pointer transition-colors bg-[#F8FAFC] hover:bg-[#EFF6FF]/50"
                    data-testid="quote-image-dropzone"
                  >
                    <Upload className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                    <p className="text-sm text-[#64748B]">
                      <span className="text-[#3B82F6] font-medium">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-[#94A3B8] mt-1">PNG, JPG, PDF up to 10MB each</p>
                  </div>

                  {/* Image Previews */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                      {images.map((img) => (
                        <div key={img.name} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden border border-slate-200">
                            <img
                              src={img.preview}
                              alt={`Upload preview`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImage(images.indexOf(img))}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                            data-testid={`remove-image-${img.name}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {images.length < 5 && (
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-slate-200 hover:border-[#3B82F6]/50 flex items-center justify-center cursor-pointer transition-colors"
                        >
                          <ImageIcon className="w-6 h-6 text-[#94A3B8]" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#0F172A] font-medium">
                    Additional Details
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about any specific requirements or questions..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="bg-[#F8FAFC] border-slate-200 focus:border-[#3B82F6] focus:ring-[#3B82F6]/20 resize-none"
                    data-testid="quote-message-input"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 btn-shine"
                  data-testid="quote-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Request Quote
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default QuoteFormSection;
